import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, X, Send, MessageCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { waLink } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["¿Qué ventajas tiene el Brangus en el trópico?", "¿Cómo funciona un convenio de embriones?", "¿Qué genética de Agucho tienen?"];
const GREETING = { role: "assistant", text: "Hola, soy el Asesor Brangus de Hacienda Fernández & Vargas. ¿En qué puedo ayudarle?" };

const sessionId = () => {
  let id = localStorage.getItem("fv_chat_sid");
  if (!id) { id = `s-${crypto.randomUUID()}`; localStorage.setItem("fv_chat_sid", id); }
  return id;
};

async function streamReply(message, page, onDelta) {
  const res = await fetch(`${API}/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message, page }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo enviar el mensaje.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const data = ev.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const j = JSON.parse(data);
      if (j.error) throw new Error(j.error);
      onDelta(j.t);
    }
  }
}

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-msg-${i}`}>
    <p className={`max-w-[85%] whitespace-pre-line px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#8B2B22] text-white rounded-2xl rounded-br-sm" : "bg-white text-[#1A1E1B] rounded-2xl rounded-bl-sm border border-[#8B2B22]/10"}`}>
      {m.text || <span className="inline-flex gap-1"><span className="animate-pulse">●</span><span className="animate-pulse [animation-delay:150ms]">●</span><span className="animate-pulse [animation-delay:300ms]">●</span></span>}
    </p>
  </div>
);

export const AdvisorChat = () => {
  const { settings } = useApp();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(() => JSON.parse(localStorage.getItem("fv_chat_msgs") || "null") || [GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs, open]);
  useEffect(() => { if (!busy) localStorage.setItem("fv_chat_msgs", JSON.stringify(msgs)); }, [msgs, busy]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const patch = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
    try {
      await streamReply(q, pathname, (t) => patch((last) => ({ ...last, text: last.text + t })));
    } catch (e) {
      patch(() => ({ role: "assistant", text: e.message }));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="advisor-chat-toggle" aria-label="Asesor Brangus"
        className="fixed bottom-20 right-4 sm:bottom-24 sm:right-5 z-50 flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#1D3324] text-[#C5A059] shadow-xl ring-2 ring-[#C5A059]/40 transition-transform duration-300 hover:scale-110">
        {open ? <X size={24} /> : <Bot size={26} />}
      </button>
      {open && (
        <div data-testid="advisor-chat-panel" className="fixed bottom-40 right-5 z-50 flex h-[min(560px,65vh)] w-[min(380px,calc(100vw-2.5rem))] flex-col overflow-hidden bg-[#F8F5EE] shadow-2xl border border-[#8B2B22]/15 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center justify-between bg-[#1D3324] px-4 py-3 text-stone-100">
            <div>
              <p className="f-display text-lg leading-tight">Asesor Brangus</p>
              <p className="text-[11px] text-stone-400">Respuestas automáticas con IA · orientativas</p>
            </div>
            <a href={waLink(settings.whatsapp, "Hola, vengo del Asesor Brangus del sitio y quisiera consultar.")} target="_blank" rel="noreferrer" data-testid="advisor-chat-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-[#0b2b17]"><MessageCircle size={14} />WhatsApp</a>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="advisor-chat-messages">
            {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {msgs.length === 1 && (
              <div className="flex flex-col items-start gap-2 pt-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => send(s)} data-testid={`advisor-suggestion-${i}`} className="rounded-full border border-[#8B2B22]/25 bg-white px-3 py-1.5 text-left text-xs text-[#8B2B22] hover:bg-[#8B2B22] hover:text-white transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-[#8B2B22]/10 bg-white p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder="Escriba su consulta…" data-testid="advisor-chat-input"
              className="flex-1 bg-transparent px-2 py-2 text-sm outline-none" />
            <button disabled={busy || !input.trim()} data-testid="advisor-chat-send" aria-label="Enviar" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#8B2B22] text-white disabled:opacity-40"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
};
